import {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import type { MutableRefObject } from "react";
import { Box3, Vector3 } from "three";
import type { Camera, Group } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { extractSilhouette, type SilhouetteParams } from "../../utils/silhouetteExtraction";

export interface CameraParams {
    fov: number;
    projectionType: "perspective" | "orthographic";
    distance: number;
}

type ViewMode = "preview" | "result";

interface SilhouetteExtractorState {
    model: Group | null;
    modelName: string;
    isLoadingModel: boolean;
    loadError: string | null;
    cameraParams: CameraParams;
    silhouetteParams: SilhouetteParams;
    generatedSvg: string | null;
    isGenerating: boolean;
    viewMode: ViewMode;
    cameraRef: MutableRefObject<Camera | null>;
    loadModel: (file: File) => Promise<void>;
    setCameraParams: (partial: Partial<CameraParams>) => void;
    setSilhouetteParams: (partial: Partial<SilhouetteParams>) => void;
    generateSilhouette: () => void;
    setViewMode: (mode: ViewMode) => void;
    setLoadError: (e: string | null) => void;
    canExport: boolean;
    handleDownload: () => void;
    getPresetValues: () => Record<string, unknown>;
    applyPreset: (values: Record<string, unknown>) => void;
    resetParameters: () => void;
}

const SilhouetteExtractorContext = createContext<SilhouetteExtractorState | null>(null);

const DEFAULT_CAMERA_PARAMS: CameraParams = {
    fov: 45,
    projectionType: "perspective",
    distance: 5,
};

const DEFAULT_SILHOUETTE_PARAMS: SilhouetteParams = {
    strokeWidth: 1.5,
    strokeColor: "#000000",
    includeCreases: false,
    creaseAngleDeg: 30,
    outputWidth: 600,
    outputHeight: 600,
    unionPaths: false,
};

const DEFAULT_PRESET: Record<string, unknown> = {
    cameraFov: 45,
    cameraProjectionType: "perspective",
    cameraDistance: 5,
    strokeWidth: 1.5,
    strokeColor: "#000000",
    includeCreases: false,
    creaseAngleDeg: 30,
    outputWidth: 600,
    outputHeight: 600,
    unionPaths: false,
};

export function useSilhouetteExtractor() {
    const ctx = useContext(SilhouetteExtractorContext);
    if (!ctx) throw new Error("useSilhouetteExtractor must be used within SilhouetteExtractorProvider");
    return ctx;
}

async function loadModelByExtension(ext: string, url: string): Promise<Group | null> {
    if (ext === "gltf" || ext === "glb") {
        const loader = new GLTFLoader();
        return new Promise<Group>((resolve, reject) => {
            loader.load(url, (data) => resolve(data.scene), undefined, (err) => {
                const msg = err instanceof Error ? err.message : String(err);
                reject(new Error(
                    msg.includes("DRACOLoader")
                        ? "This model uses Draco compression, which isn't supported yet. Try exporting without Draco."
                        : msg,
                ));
            });
        });
    }
    if (ext === "obj") {
        const loader = new OBJLoader();
        return new Promise<Group>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
        });
    }
    if (ext === "fbx") {
        const loader = new FBXLoader();
        return new Promise<Group>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
        });
    }
    return null;
}

export function SilhouetteExtractorProvider({ children }: { children: React.ReactNode }) {
    const [model, setModel] = useState<Group | null>(null);
    const [modelName, setModelName] = useState("");
    const [isLoadingModel, setIsLoadingModel] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [cameraParams, setCameraParamsState] = useState<CameraParams>(DEFAULT_CAMERA_PARAMS);
    const [silhouetteParams, setSilhouetteParamsState] = useState<SilhouetteParams>(DEFAULT_SILHOUETTE_PARAMS);
    const [generatedSvg, setGeneratedSvg] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>("preview");

    // Written by the viewer on mount; read by generateSilhouette to access the live camera
    const cameraRef = useRef<Camera | null>(null);

    const setCameraParams = useCallback((partial: Partial<CameraParams>) => {
        setCameraParamsState((prev) => ({ ...prev, ...partial }));
    }, []);

    const setSilhouetteParams = useCallback((partial: Partial<SilhouetteParams>) => {
        setSilhouetteParamsState((prev) => ({ ...prev, ...partial }));
    }, []);

    const loadModel = useCallback(async (file: File) => {
        setIsLoadingModel(true);
        setLoadError(null);
        setGeneratedSvg(null);
        setViewMode("preview");
        const url = URL.createObjectURL(file);
        try {
            const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
            const loadedScene = await loadModelByExtension(ext, url);
            if (!loadedScene) throw new Error(`Unsupported format: .${ext}`);
            // Normalize once at load time so the viewer and extractSilhouette
            // both see the same geometry (centered at origin, ~2.5-unit radius).
            loadedScene.updateMatrixWorld(true);
            const box = new Box3().setFromObject(loadedScene);
            const center = box.getCenter(new Vector3());
            const size = box.getSize(new Vector3());
            const maxDim = Math.max(size.x, size.y, size.z, 0.001);
            const normalizeScale = 2.5 / maxDim;
            loadedScene.scale.setScalar(normalizeScale);
            loadedScene.position.copy(center).multiplyScalar(-normalizeScale);
            setModel(loadedScene);
            setModelName(file.name);
        } catch (e) {
            setLoadError(e instanceof Error ? e.message : `Failed to load model: ${String(e)}`);
        } finally {
            URL.revokeObjectURL(url);
            setIsLoadingModel(false);
        }
    }, []);

    const generateSilhouette = useCallback(() => {
        if (!model || !cameraRef.current) return;
        setIsGenerating(true);
        setLoadError(null);
        try {
            const svg = extractSilhouette(model, cameraRef.current, silhouetteParams);
            setGeneratedSvg(svg);
            setViewMode("result");
        } catch (e) {
            setLoadError(`Generation failed: ${e instanceof Error ? e.message : String(e)}`);
        } finally {
            setIsGenerating(false);
        }
    }, [model, silhouetteParams]);

    const handleDownload = useCallback(() => {
        if (!generatedSvg) return;
        const blob = new Blob([generatedSvg], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `silhouette-${modelName.replace(/\.[^.]+$/, "") || "export"}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [generatedSvg, modelName]);

    const getPresetValues = useCallback((): Record<string, unknown> => ({
        cameraFov: cameraParams.fov,
        cameraProjectionType: cameraParams.projectionType,
        cameraDistance: cameraParams.distance,
        strokeWidth: silhouetteParams.strokeWidth,
        strokeColor: silhouetteParams.strokeColor,
        includeCreases: silhouetteParams.includeCreases,
        creaseAngleDeg: silhouetteParams.creaseAngleDeg,
        outputWidth: silhouetteParams.outputWidth,
        outputHeight: silhouetteParams.outputHeight,
        unionPaths: silhouetteParams.unionPaths,
    }), [cameraParams, silhouetteParams]);

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        const cam: Partial<CameraParams> = {};
        if (values.cameraFov != null) cam.fov = values.cameraFov as number;
        if (values.cameraProjectionType != null) cam.projectionType = values.cameraProjectionType as CameraParams["projectionType"];
        if (values.cameraDistance != null) cam.distance = values.cameraDistance as number;
        if (Object.keys(cam).length > 0) setCameraParamsState((prev) => ({ ...prev, ...cam }));

        const sil: Partial<SilhouetteParams> = {};
        if (values.strokeWidth != null) sil.strokeWidth = values.strokeWidth as number;
        if (values.strokeColor != null) sil.strokeColor = values.strokeColor as string;
        if (values.includeCreases != null) sil.includeCreases = values.includeCreases as boolean;
        if (values.creaseAngleDeg != null) sil.creaseAngleDeg = values.creaseAngleDeg as number;
        if (values.outputWidth != null) sil.outputWidth = values.outputWidth as number;
        if (values.outputHeight != null) sil.outputHeight = values.outputHeight as number;
        if (values.unionPaths != null) sil.unionPaths = values.unionPaths as boolean;
        if (Object.keys(sil).length > 0) setSilhouetteParamsState((prev) => ({ ...prev, ...sil }));
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "silhouette-extractor",
        defaultPreset: DEFAULT_PRESET,
        getPresetValues,
        applyPreset,
    });

    const value: SilhouetteExtractorState = {
        model,
        modelName,
        isLoadingModel,
        loadError,
        cameraParams,
        silhouetteParams,
        generatedSvg,
        isGenerating,
        viewMode,
        cameraRef,
        loadModel,
        setCameraParams,
        setSilhouetteParams,
        generateSilhouette,
        setViewMode,
        setLoadError,
        canExport: generatedSvg !== null,
        handleDownload,
        getPresetValues,
        applyPreset,
        resetParameters,
    };

    return (
        <SilhouetteExtractorContext.Provider value={value}>
            {children}
        </SilhouetteExtractorContext.Provider>
    );
}
